/**
 * Client-scoped IndexedDB access (multi-client plan).
 *
 * Local persistence caches CLIENT DATA (records, uploads, captures,
 * questionnaire responses, chat sessions), so with multi-client on every
 * key gets an `:<clientId>` suffix — switching clients then lands on a
 * different, isolated cache namespace, and client A's cached data can
 * never hydrate into a session viewing client B.
 *
 * With the flag off (or before resolution) keys pass through UNCHANGED,
 * so single-instance deployments and the seed-mode smoke tests are
 * byte-identical. ClientProvider guarantees hydration only starts after
 * the active client is resolved, so a scoped module never reads under
 * one key and writes under another.
 *
 * Swap `get/set/del from "idb-keyval"` for these in any module whose keys
 * hold client data. Global user prefs (theme, auth roster, power-user)
 * stay on raw idb-keyval / storage — they are the user's, not a client's.
 */

import {
  del as idbDel,
  get as idbGet,
  set as idbSet,
} from "idb-keyval";
import { getActiveClientId } from "@/lib/active-client";

/** The storage key for `base` under the active client (pass-through when
 *  multi-client is off or no client is resolved). */
export function scopedKey(base: string): string {
  const clientId = getActiveClientId();
  return clientId ? `${base}:${clientId}` : base;
}

export function scopedIdbGet<T = unknown>(base: string): Promise<T | undefined> {
  return idbGet<T>(scopedKey(base));
}

export function scopedIdbSet(base: string, value: unknown): Promise<void> {
  return idbSet(scopedKey(base), value);
}

export function scopedIdbDel(base: string): Promise<void> {
  return idbDel(scopedKey(base));
}
